import { all, put, select, takeLatest, call } from 'redux-saga/effects';

import api from '../../services/api';
import {
  generateToken,
  getUserConfirmedData,
  validateToken,
  updatePercentage,
  setUserConfirmedData,
  setEmailConfirmado,
  setTelefoneConfirmado,
  setEnderecoConfirmado,
  generateNewTelephone,
  validateNewTelephone,
} from './actions';
import { percentagemStatus } from '../../services/utils/percentageStatusProfile';

function* generateTokenSaga() {
  try {
    const selectedContact = yield select(
      (state: ApplicationState) => state.contacts.selected
    );
    const { type } = selectedContact as ContactType;
    const { cod_email, databaseId: dbIdMail } = selectedContact as GQL.IEmail;
    const { cod_tel, databaseId: dbIdTel } = selectedContact as GQL.ITelephone;
    const cpf = yield select((state: ApplicationState) => state.auth.cpf);
    const result = yield api.generateToken({
      data: {
        contactId: cod_email || cod_tel || null,
        contactType: type,
        cpf: cpf as string,
        databaseId: dbIdMail || dbIdTel || 0,
      },
    });
    yield put(generateToken.success(result));
  } catch (err) {
    yield put(generateToken.failure(err.message));
  }
}

function* validateTokenSaga(action: ReturnType<typeof validateToken.request>) {
  try {
    const payload = yield select((state: ApplicationState) => {
      const { type } = (state.contacts?.selected as ContactType) ?? {
        type: 'email',
      };
      const { cod_email, databaseId: dbIdMail } = (state.contacts
        ?.selected as GQL.IEmail) ?? { cod_email: null, databaseId: null };
      const { cod_tel, databaseId: dbIdTel } = (state.contacts
        ?.selected as GQL.ITelephone) ?? { cod_tel: null, databaseId: null };
      const userData = state.contactsByCpf?.user as IUserByCpf[];
      let databaseId: number | undefined;

      const cpf = state.auth?.cpf;
      const creditorId = state.validate?.user.creditorId ?? null;

      switch (state.config.validacaoAcesso) {
        case '24':
        case '26':
        case '27':
          databaseId = userData[0]?.databaseId;
          break;
        default:
          databaseId = dbIdMail || dbIdTel || 0;
      }

      return {
        contactId: cod_email || cod_tel,
        contactType: type,
        cpf,
        databaseId: databaseId,
        token: action.payload,
        tipoAcesso: state.config.tipoAcesso,
        creditorId: creditorId,
      };
    });

    const result: ValidationResponseDto = yield api.validateToken(payload);
    api.setToken(result.token);
    yield put(validateToken.success(result));
    yield call(getUserConfirmedDataSaga);
  } catch (error) {
    yield put(validateToken.failure(error.message));
  }
}

function* getUserConfirmedDataSaga() {
  try {
    const data: GQL.IUserResponse = yield call(api.getUserConfirmedData);
    const percentage = percentagemStatus(data);
    yield put(updatePercentage(percentage));
    yield put(getUserConfirmedData.success(data));
  } catch {
    yield put(getUserConfirmedData.failure('Erro ao obter dados confirmados.'));
  }
}

function* setUserConfirmedDataSaga(
  action: ReturnType<typeof setUserConfirmedData.request>
) {
  try {
    const data = yield call(api.setUserConfirmedData, action.payload.data);
    if (data === 'ok') {
      yield put(setUserConfirmedData.success('ok'));
    } else {
      yield put(setUserConfirmedData.failure('Erro ao atualizar os dados'));
    }
  } catch {
    yield put(setUserConfirmedData.failure('Erro ao obter dados confirmados.'));
  }
}

function* setEmailConfirmadoSaga(
  action: ReturnType<typeof setEmailConfirmado.request>
) {
  try {
    const data = yield call(api.setEmailConfirmado, action.payload.data);
    if (data === 'ok') {
      yield put(setEmailConfirmado.success('ok'));
    } else {
      yield put(setEmailConfirmado.failure('Erro ao atualizar os dados'));
    }
  } catch {
    yield put(setEmailConfirmado.failure('Erro ao obter dados confirmados.'));
  }
}

function* setTelefoneConfirmadoSaga(
  action: ReturnType<typeof setTelefoneConfirmado.request>
) {
  try {
    const data = yield call(api.setTelefoneConfirmado, action.payload.data);
    if (data === 'ok') {
      yield put(setTelefoneConfirmado.success('ok'));
    } else {
      yield put(setTelefoneConfirmado.failure('Erro ao atualizar os dados'));
    }
  } catch {
    yield put(
      setTelefoneConfirmado.failure('Erro ao obter dados confirmados.')
    );
  }
}

function* setEnderecoConfirmadoSaga(
  action: ReturnType<typeof setEnderecoConfirmado.request>
) {
  try {
    const data = yield call(api.setEnderecoConfirmado, action.payload.data);
    if (data === 'ok') {
      yield put(setEnderecoConfirmado.success('ok'));
    } else {
      yield put(setEnderecoConfirmado.failure('Erro ao atualizar os dados'));
    }
  } catch {
    yield put(
      setEnderecoConfirmado.failure('Erro ao obter dados confirmados.')
    );
  }
}

function* generateNewTelephoneSaga(
  action: ReturnType<typeof generateNewTelephone.request>
): Generator {
  try {
    const contactsList = yield select(
      (state: ApplicationState) => state?.contacts?.list
    );
    const databaseId = yield select(
      (state: ApplicationState) => state?.contactsByCpf?.user?.[0]?.databaseId
    );
    const cpf = yield select((state: ApplicationState) => state.auth.cpf);
    const contact = (contactsList as UserContact[])?.[0];
    const contactsDatabaseId = (contact as GQL.IEmail | GQL.ITelephone)
      ?.databaseId as number;

    const type =
      action.payload.type === 'whatsapp'
        ? ('Whatsapp' as GQL.ContactType)
        : action.payload.type === 'sms'
        ? ('Sms' as GQL.ContactType)
        : '';

    if (!type) return yield put(generateNewTelephone.failure(false));

    const args: GQL.IGenerateTokenInput = {
      contactType: type,
      cpf: cpf as string,
      databaseId: contactsDatabaseId || (databaseId as number),
      contactId: null,
    };

    const data = yield call(
      api.generateNewTelephone,
      args,
      action.payload.telephone
    );

    if (data === 'ok') {
      yield put(generateNewTelephone.success(true));
    } else {
      yield put(generateNewTelephone.failure(false));
    }
  } catch {
    yield put(generateNewTelephone.failure(false));
  }
}

function* validateNewTelephoneSaga(
  action: ReturnType<typeof validateNewTelephone.request>
) {
  try {
    const data = yield call(api.validateNewTelephone, action.payload);
    if (data === 'ok') {
      yield put(validateNewTelephone.success(true));
    } else {
      yield put(validateNewTelephone.failure(false));
    }
  } catch {
    yield put(validateNewTelephone.failure(false));
  }
}

export default all([
  takeLatest(generateToken.request, generateTokenSaga),
  takeLatest(validateToken.request, validateTokenSaga),
  takeLatest(getUserConfirmedData.request, getUserConfirmedDataSaga),
  takeLatest(setUserConfirmedData.request, setUserConfirmedDataSaga),
  takeLatest(setEmailConfirmado.request, setEmailConfirmadoSaga),
  takeLatest(setTelefoneConfirmado.request, setTelefoneConfirmadoSaga),
  takeLatest(setEnderecoConfirmado.request, setEnderecoConfirmadoSaga),
  takeLatest(generateNewTelephone.request, generateNewTelephoneSaga),
  takeLatest(validateNewTelephone.request, validateNewTelephoneSaga),
]);
